import React, { useState } from 'react';
import CareerPathwayDropdown from './CareerPathwayDropdown';
import { EditIcon } from '@/components/DesignSystem/Icons';
import { CareerDetailsProps } from '../types';
import tagularEvent from '@/utils/tagular/tagularEvent';
import { Tooltip } from '@/components/Tooltip';
import { Icon } from '@/components/Icon';

const CareerDetails = ({
  currentEducationOptions,
  categories,
  currentRoleOptions,
  targetRoleOptions,
  selectedCurrentEducation,
  selectedFieldOfStudy,
  selectedCurrentRole,
  selectedTargetRole,
  loading,
  onEditClick,
  onCurrentEducationChange,
  onFieldOfStudyChange,
  onCurrentRoleChange,
  onTargetRoleChange,
  onSubmit,
  handleEditDetailsClick,
  correlationId,
}: CareerDetailsProps) => {
  const [editOpen, setEditOpen] = useState(false);

  const isFormComplete =
    selectedCurrentEducation &&
    selectedFieldOfStudy &&
    selectedCurrentRole &&
    selectedTargetRole;

  const handleEditClick = () => {
    setEditOpen(!editOpen);
    onEditClick();

    // Generate new correlationId for this edit session
    const newCorrelationId = handleEditDetailsClick();

    // Fire tagular event for edit details click with new correlationId
    try {
      tagularEvent('ElementClicked', {
        correlationId: newCorrelationId,
        webElement: {
          elementType: 'button',
          location: 'career-pathways',
          name: 'edit-cta',
          text: 'edit details',
        },
      });
    } catch (error) {
      console.error('Error firing edit details tagular event:', error);
    }
  };

  const handleSubmit = () => {
    onSubmit('CareerDetails');
    setEditOpen(false);
  };

  return (
    <div className='w-full bg-gray-50 rounded-lg p-5'>
      {/* Header */}
      <div className='flex justify-between items-center mb-2 md:mb-5'>
        <div className='flex flex-col gap-2'>
          <div className='flex items-center gap-1'>
            <span className='text-base md:text-lg font-bold text-black'>
              Your Information
            </span>
            <Tooltip
              label={
                <Icon
                  icon='information-circle'
                  width='16'
                  height='16'
                  color='#098180'
                />
              }
              position='bottom'
            >
              This section reflects the education and career details you’ve
              entered. These inputs guide your personalized pathway and the
              programs we recommend. Edit anytime to explore different outcomes.
            </Tooltip>
          </div>

          {editOpen && (
            <span className='text-base text-gray-600'>
              Edit your details below to update your results.
            </span>
          )}
        </div>

        {!editOpen && (
          <button
            onClick={handleEditClick}
            className='font-normal flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors'
          >
            <EditIcon />
            <span className='font-medium md:flex hidden'>Edit Details</span>
          </button>
        )}

        {editOpen && (
          <div className='flex justify-end'>
            <button
              className={`px-6 py-3 rounded-lg font-semibold text-sm transition-colors ${
                isFormComplete && !loading
                  ? 'bg-primary-700 text-white hover:bg-primary-800'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
              onClick={handleSubmit}
              disabled={!isFormComplete || loading}
            >
              {loading ? 'Updating...' : 'Update Results'}
            </button>
          </div>
        )}
      </div>

      {/* Information Cards */}
      <div className='grid grid-cols-1  md:grid-cols-2 gap-2 text-xs md:text-sm'>
        <CareerPathwayDropdown
          label='Current Education'
          options={currentEducationOptions}
          selectedOption={selectedCurrentEducation}
          onOptionChange={onCurrentEducationChange}
          placeholder='Select current education'
          getOptionValue={(option) => option.id?.toString() || ''}
          getOptionDisplay={(option) => option.name || ''}
          isEditable={editOpen}
          viewCorrelationId={correlationId}
        />

        <CareerPathwayDropdown
          label='Field of Study'
          options={categories}
          selectedOption={selectedFieldOfStudy}
          onOptionChange={onFieldOfStudyChange}
          placeholder='Select field of study'
          getOptionValue={(option) => option.id?.toString() || ''}
          getOptionDisplay={(option) => option.name || ''}
          isEditable={editOpen}
          viewCorrelationId={correlationId}
        />

        <CareerPathwayDropdown
          label='Current Role'
          options={currentRoleOptions}
          selectedOption={selectedCurrentRole}
          onOptionChange={onCurrentRoleChange}
          placeholder='Select current role'
          getOptionValue={(option) => option.title || ''}
          getOptionDisplay={(option) => option.title || ''}
          isEditable={editOpen}
          viewCorrelationId={correlationId}
        />

        <CareerPathwayDropdown
          label='Target Role'
          options={targetRoleOptions}
          selectedOption={selectedTargetRole}
          onOptionChange={onTargetRoleChange}
          placeholder='Select target role'
          getOptionValue={(option) => option.title || ''}
          getOptionDisplay={(option) => option.title || ''}
          isEditable={editOpen}
          viewCorrelationId={correlationId}
        />
      </div>
    </div>
  );
};

export default CareerDetails;
